import { useEffect, type RefObject } from 'react'

const COLLAPSE_TRANSITION = 'height 200ms ease-out'

/**
 * Animates a container's height to collapse/expand.
 *
 * Uses a ResizeObserver so height updates when content size changes (e.g. dynamic list).
 * The ref should be attached to a wrapper div - the hook sets overflow:hidden automatically.
 *
 * @param contentRef - Ref for the collapsible content DOM element
 * @param isExpanded - Whether the content should be expanded (visible) or collapsed (height 0)
 */
export function useCollapseAnimation(contentRef: RefObject<HTMLDivElement | null>, isExpanded: boolean): void {
  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    el.style.overflow = 'hidden'
    el.style.transition = COLLAPSE_TRANSITION

    const observer = new ResizeObserver(() => {
      if (isExpanded && contentRef.current) {
        contentRef.current.style.height = `${contentRef.current.scrollHeight}px`
      }
    })

    observer.observe(el)

    if (isExpanded) {
      el.style.height = `${el.scrollHeight}px`
    } else {
      el.style.height = '0px'
    }

    return () => observer.disconnect()
  }, [contentRef, isExpanded])
}
